/**
 * Display helpers for raw phone numbers in lists (Logs, Inbox).
 * "+447944645035" -> "+44 7944 645035", location "United Kingdom".
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';

/** International format, or the input unchanged when it does not parse. */
export function formatPhone(raw: string | null | undefined): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const p = parsePhoneNumberFromString(s.startsWith('+') ? s : `+${s.replace(/\D/g, '')}`);
  return p?.isPossible() ? p.formatInternational() : s;
}

let regionNames: Intl.DisplayNames | null | undefined;

/** Country name for a number ("United Kingdom"), or '' when unknown. */
export function phoneLocation(raw: string | null | undefined): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  const p = parsePhoneNumberFromString(s.startsWith('+') ? s : `+${s.replace(/\D/g, '')}`);
  const iso = p?.country;
  if (!iso) return '';
  if (regionNames === undefined) {
    try { regionNames = new Intl.DisplayNames(undefined, { type: 'region' }); } catch { regionNames = null; }
  }
  return regionNames?.of(iso) || iso;
}

/** True when `name` is just the number again (a contact saved without a name). */
export function isNumberLike(name: string | null | undefined, phone?: string | null): boolean {
  const n = String(name || '').trim();
  if (!n) return true;
  if (!/[a-z]/i.test(n)) return true;
  const digits = n.replace(/\D/g, '');
  return !!phone && digits.length >= 6 && digits === String(phone).replace(/\D/g, '');
}

/** Up to two initials from a name, e.g. "Ada Lovelace" -> "AL". */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter((w) => /[a-z]/i.test(w));
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}
